import { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { usePermissions } from '@/hooks/usePermissions'
import {
  AcademicCapIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  TrophyIcon,
  DocumentTextIcon,
  ArrowRightIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import { Card, CardHeader, Badge, Avatar, InlineSpinner } from '@/components/ui'
import { dashboardApi, forumApi } from '@/api'
import type { DashboardData } from '@/api/dashboard'
import type { ThreadListItem } from '@/api'

// Dashboard Widgets
import PersonalAgendaWidget from '@/components/dashboard/PersonalAgendaWidget'
import NotificationsWidget from '@/components/dashboard/NotificationsWidget'
import CourseProgressWidget from '@/components/dashboard/CourseProgressWidget'
import GamificationWidget from '@/components/dashboard/GamificationWidget'
import TaskForceWidget from '@/components/dashboard/TaskForceWidget'

export default function DashboardPage() {
  const { user } = useAuthStore()
  const { hasPermission } = usePermissions()
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [recentThreads, setRecentThreads] = useState<ThreadListItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [dashboard, threadsRes] = await Promise.allSettled([
          dashboardApi.getDashboardData(),
          forumApi.getThreads({ page: 1, ordering: '-created_at' }),
        ])

        if (dashboard.status === 'fulfilled') {
          setDashboardData(dashboard.value)
        }

        if (threadsRes.status === 'fulfilled' && threadsRes.value?.results) {
          setRecentThreads(threadsRes.value.results.slice(0, 5))
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [])

  // Stat cards filtrate in base ai permessi DINAMICI dal database
  const statCards = useMemo(() => {
    const allCards = [
      {
        title: 'Corsi in svolgimento',
        value: dashboardData?.courses?.in_progress?.length || 0,
        icon: AcademicCapIcon,
        bgColor: 'bg-blue-100',
        iconColor: 'text-blue-600',
        link: '/courses',
        permission: 'lms.view',
      },
      {
        title: 'Discussioni',
        value: dashboardData?.forum?.threads || 0,
        icon: ChatBubbleLeftRightIcon,
        bgColor: 'bg-green-100',
        iconColor: 'text-green-600',
        link: '/forum',
        permission: 'forum.view',
      },
      {
        title: 'Eventi prenotati',
        value: dashboardData?.events?.upcoming?.length || 0,
        icon: CalendarIcon,
        bgColor: 'bg-purple-100',
        iconColor: 'text-purple-600',
        link: '/events',
        permission: 'events.view',
      },
      {
        title: 'Punti',
        value: dashboardData?.gamification?.points || 0,
        icon: TrophyIcon,
        bgColor: 'bg-yellow-100',
        iconColor: 'text-yellow-600',
        link: '/leaderboard',
        subtitle: `Livello ${dashboardData?.gamification?.level || 1}`,
        permission: 'gamification.view',
      },
    ]
    // Filtra le card in base ai permessi dell'utente
    return allCards.filter(card => hasPermission(card.permission))
  }, [dashboardData, hasPermission])

  if (loading) {
    return <InlineSpinner message="Caricamento dashboard..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Benvenuto, {user?.first_name || 'Utente'}!
          </h1>
          <p className="text-gray-600">Ecco il riepilogo delle tue attivita</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Link key={stat.title} to={stat.link}>
            <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
              <div className="flex items-center">
                <div className={`p-3 ${stat.bgColor} rounded-lg`}>
                  <stat.icon className={`h-6 w-6 ${stat.iconColor}`} />
                </div>
                <div className="ml-4">
                  <p className="text-sm text-gray-500">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  {stat.subtitle && (
                    <p className="text-xs text-gray-400">{stat.subtitle}</p>
                  )}
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      {/* Quick Links - FILTRATE in base ai permessi DINAMICI dal database */}
      {/* Ogni link appare SOLO se l'utente ha il permesso corrispondente */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="quick-links-section">
        {hasPermission('services.view') && (
          <Link
            to="/clinic"
            className="flex flex-col items-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-100"
          >
            <div className="p-3 bg-green-100 rounded-full">
              <DocumentTextIcon className="h-6 w-6 text-green-600" />
            </div>
            <span className="mt-2 text-sm font-medium text-gray-900">
              Compliance Clinic
            </span>
          </Link>
        )}
        {hasPermission('documents.view') && (
          <Link
            to="/documents"
            className="flex flex-col items-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-100"
          >
            <div className="p-3 bg-purple-100 rounded-full">
              <DocumentTextIcon className="h-6 w-6 text-purple-600" />
            </div>
            <span className="mt-2 text-sm font-medium text-gray-900">
              Documenti
            </span>
          </Link>
        )}
        {hasPermission('taskforce.view') && (
          <Link
            to="/taskforce"
            className="flex flex-col items-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-100"
          >
            <div className="p-3 bg-orange-100 rounded-full">
              <UserGroupIcon className="h-6 w-6 text-orange-600" />
            </div>
            <span className="mt-2 text-sm font-medium text-gray-900">
              Task Force
            </span>
          </Link>
        )}
      </div>

      {/* Main Content - 3 Column Grid */}
      {/* Ogni widget appare SOLO se l'utente ha il permesso corrispondente */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Courses & Gamification */}
        <div className="space-y-6">
          {hasPermission('lms.view') && (
            <CourseProgressWidget
              courses={dashboardData?.courses?.in_progress}
              completedCount={dashboardData?.courses?.completed_count}
            />
          )}
          {hasPermission('gamification.view') && (
            <GamificationWidget
              points={dashboardData?.gamification?.points}
              level={dashboardData?.gamification?.level}
              badgesCount={dashboardData?.gamification?.badges_count}
              rank={dashboardData?.gamification?.rank}
            />
          )}
        </div>

        {/* Center Column - Agenda & Forum */}
        <div className="space-y-6">
          {hasPermission('events.view') && <PersonalAgendaWidget />}

          {/* Recent Forum Activity - solo se ha forum.view */}
          {hasPermission('forum.view') && (
            <Card>
              <CardHeader
                title={
                  <div className="flex items-center gap-2">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 text-green-500" />
                    <span>Ultime discussioni</span>
                  </div>
                }
                action={
                  <Link
                    to="/forum"
                    className="text-sm text-primary-600 hover:text-primary-700 flex items-center"
                  >
                    Forum <ArrowRightIcon className="h-4 w-4 ml-1" />
                  </Link>
                }
              />
              {recentThreads.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {recentThreads.map((thread) => (
                    <Link
                      key={thread.id}
                      to={`/forum/thread/${thread.id}`}
                      className="block py-3 hover:bg-gray-50 transition-colors -mx-4 px-4 first:-mt-2 last:-mb-2"
                    >
                      <div className="flex items-start space-x-3">
                        <Avatar
                          size="sm"
                          name={thread.author.display_name}
                          src={thread.author.avatar_url || undefined}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {thread.title}
                          </p>
                          <div className="flex items-center mt-1 text-xs text-gray-500">
                            <span>{thread.author.display_name}</span>
                            <span className="mx-2">•</span>
                            <Badge size="sm">{thread.forum_name}</Badge>
                          </div>
                        </div>
                        <span className="flex items-center text-xs text-gray-400">
                          <ChatBubbleLeftRightIcon className="h-3 w-3 mr-1" />
                          {thread.posts_count}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">
                  Nessuna discussione recente
                </p>
              )}
            </Card>
          )}
        </div>

        {/* Right Column - Notifications & Task Force */}
        <div className="space-y-6">
          <NotificationsWidget
            notifications={dashboardData?.notifications?.recent}
            unreadCount={dashboardData?.notifications?.unread_count}
          />
          {hasPermission('taskforce.view') && (
            <TaskForceWidget
              pendingTasks={dashboardData?.taskforce?.pending_tasks}
            />
          )}
        </div>
      </div>
    </div>
  )
}
